import React, { useEffect, useState } from 'react';
import { Button, LinearProgress, styled } from '@mui/material';
import {
	DataGridPro,
	DataGridProProps,
	GridApiRef,
	GridCallbackDetails,
	GridCellParams,
	GridPinnedColumns,
	GridRowModel,
	GridRowParams,
	GridSortModel,
	MuiEvent,
} from '@mui/x-data-grid-pro';
import { merge } from 'lodash';
import { getDataGridLocales } from 'services/localization/getDataGridLocales';
import { useTranslation } from 'services/localization';
import EmptyState from 'common/components/states/EmptyState';
import CustomNoRowsOverlay from './CustomNoRowsOverlay';
import CustomColumnsPanel from './CustomColumnsPanel';

export interface RentleDataGridProps<RowType extends GridRowModel> extends DataGridProProps {
	apiRef: GridApiRef;
	defaultSortModel?: GridSortModel;
	rows: RowType[];
}

export const RentleDataGrid = <RowType extends GridRowModel>(
	props: RentleDataGridProps<RowType>,
) => {
	const { t } = useTranslation();
	const { apiRef, onCellClick, onRowClick, defaultSortModel } = props;

	useEffect(() => {
		setPageSize(props.pageSize);
	}, [props.pageSize]);

	const [pinnedColumns, setPinnedColumns] = useState<GridPinnedColumns>(
		merge({ left: ['__check__'] }, props.pinnedColumns),
	);

	const [sortModel, setSortModel] = useState<GridSortModel | undefined>(defaultSortModel);
	const [pageSize, setPageSize] = useState<number | undefined>(props.pageSize);

	/**
	 * Augment default onCellClick to select the row if clicked anywhere in the checkbox column
	 */
	const handleCellClick = (
		params: GridCellParams,
		event: MuiEvent<React.MouseEvent<HTMLElement, MouseEvent>>,
		details: GridCallbackDetails,
	) => {
		if (params.field === '__check__') {
			event.stopPropagation();
			const isSelected = apiRef.current.isRowSelected(params.id);
			apiRef.current.selectRow(params.id, !isSelected);
		}

		onCellClick?.(params, event, details);
	};

	/**
	 * Augment default onRowClick to select/unselect a row on click if there are already some rows selected
	 */
	const handleRowClick = (
		params: GridRowParams,
		event: MuiEvent<React.MouseEvent<HTMLElement, MouseEvent>>,
		details: GridCallbackDetails,
	) => {
		const rowId = params.id;
		const selectedRows = apiRef.current.getSelectedRows();

		if (selectedRows.size !== 0) {
			const isSelected = apiRef.current.isRowSelected(rowId);
			apiRef.current.selectRow(rowId, !isSelected);
		} else {
			onRowClick?.(params, event, details);
		}
	};

	/**
	 * Augment default pinned column behaviour so that checkbox column is always pinned to the left
	 */
	const handlePinnedColumnsChange = (
		pinnedColumns: GridPinnedColumns,
		details: GridCallbackDetails,
	) => {
		const newPinnedColumns = merge({ left: ['__check__'] }, pinnedColumns);
		setPinnedColumns(newPinnedColumns);
		props.onPinnedColumnsChange?.(newPinnedColumns, details);
	};

	return (
		<StyledDataGrid
			{...props}
			disableSelectionOnClick
			disableColumnReorder
			checkboxSelection={props.checkboxSelection ?? true}
			onCellClick={handleCellClick}
			onRowClick={handleRowClick}
			pinnedColumns={pinnedColumns}
			onPinnedColumnsChange={handlePinnedColumnsChange}
			sortModel={sortModel}
			onSortModelChange={setSortModel}
			localeText={getDataGridLocales(t)}
			pageSize={pageSize}
			onPageSizeChange={(size, details) => {
				setPageSize(size);
				props.onPageSizeChange?.(size, details);
			}}
			pagination={props.pagination}
			components={{
				ErrorOverlay,
				LoadingOverlay: LinearProgress,
				NoRowsOverlay: CustomNoRowsOverlay,
				ColumnsPanel: CustomColumnsPanel,
				...props.components,
			}}
		/>
	);
};

const ErrorOverlay = () => {
	const { t } = useTranslation();
	return (
		<EmptyState
			title={t('common:errors.somethingWentWrongShort')}
			description={t('common:errors.pleaseReloadPage')}
			cta={<Button onClick={() => window.location.reload()}>{t('common:actions.refresh')}</Button>}
		/>
	);
};

const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
	backgroundColor: theme.palette.background.paper,
	'& .MuiDataGrid-columnHeaders': {
		backgroundColor: theme.palette.background.secondary,
	},
	'& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
		outline: 'none',
	},
	'& .MuiDataGrid-menuIcon': {
		visibility: 'visible',
		width: 'auto',
	},
	'& .MuiDataGrid-toolbar': {
		padding: theme.spacing(1),
	},
	'& .MuiDataGrid-columnSeparator': {
		color: theme.palette.border.table,
	},
	'& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnSeparator': {
		display: 'none',
	},
	'& .MuiDataGrid-cell': {
		border: 'none',
		fontSize: '1.4rem',
	},
	'& .MuiDataGrid-row': {
		cursor: 'pointer',
		borderBottom: `1px solid ${theme.palette.border.table}`,
		'& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
			outline: 'none',
		},
	},
	'& .overflow-cell': {
		overflowX: 'auto',
	},
	'& .no-sku-cell': {
		color: theme.palette.error.main,
		fontStyle: 'italic',
	},
	'& .MuiDataGrid-columnHeaders + div': {
		//Forces scrollbar to table bottom
		minHeight: '100%',
	},
	'& .MuiDataGrid-virtualScroller': {
		//Forces scrollbar to table bottom
		minHeight: 'calc(100% - 56px) !important',
	},
	'& .MuiDataGrid-pinnedColumns': {
		background: theme.palette.background.paper,
	},
	'& .MuiDataGrid-pinnedColumnHeaders--left': {
		background: theme.palette.background.secondary,
	},
	'& .MuiDataGrid-overlay': {
		zIndex: 10,
	},
	'& .MuiLinearProgress-root': {
		zIndex: 10,
	},
}));

export default RentleDataGrid;
